import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarGroupLabel
} from "@/components/ui/sidebar";

import { Cog, Server, LayoutDashboard, Table, Network, Settings, Loader2, Shield, ExternalLink } from "lucide-react";
import { useGlobalAdminStatus } from "@/hooks/useGlobalAdminStatus";
import NavTreeView from "./NavTreeView";
import { SidebarFooterMenu } from "./SidebarFooterMenu";
import { SidebarMenuLogo } from "./SidebarMenuLogo";
import { Link } from "react-router-dom";
import { OrgSwitcher } from "@/components/organizations/org-switcher";
import ProfileSelector from "@/components/groups/ProfileSelector";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { User } from "@/context/AuthContext";
import { useAutomationsCounts } from "@/hooks/useAutomationsCounts";

type AppSidebarProps = {
  user: User;
  path?: string;
};

export function AppSidebar(props: AppSidebarProps) {
  const { user, path } = props;
  const { isAdmin } = useAdminStatus();
  const { isGlobalAdmin } = useGlobalAdminStatus();
  const { 
    automationCount, 
    automationServerCount, 
    workspaceCount, 
    processCount,
    isLoading 
  } = useAutomationsCounts();
  
  
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuLogo />
          </SidebarMenuItem>
          <SidebarSeparator />
          <SidebarMenuItem key={"orgs"}>
            <OrgSwitcher />
          </SidebarMenuItem>
          <SidebarMenuItem key={"profiles"}>
            <ProfileSelector />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem key={"dashboard"}>
                <SidebarMenuButton asChild isActive={path === "/dashboard"}>
                  <Link to={"/dashboard"}>
                    <LayoutDashboard />
                    <span>{"Dashboard"}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              { isAdmin && (
              <SidebarMenuItem key={"automation-servers"}>
                <SidebarMenuButton asChild isActive={path === "/automation-servers"}>
                  <Link to={"/automation-servers"}>
                    <Server />
                    <span>{"Servers"}</span>
                    <span className="ml-auto text-xs">
                      { isLoading ? <Loader2 size={16} className="animate-spin ml-1" /> : automationServerCount}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              )}
              <SidebarMenuItem key={"workspaces"}>
                <SidebarMenuButton asChild isActive={path === "/workspaces"}>
                  <Link to={"/workspaces"}>
                    <Table />
                    <span>{"Workspaces"}</span>
                    <span className="ml-auto text-xs">
                      { isLoading ? <Loader2 size={16} className="animate-spin ml-1" /> : workspaceCount}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem key={"processes"}>
                <SidebarMenuButton asChild isActive={path === "/processes"}>
                  <Link to={"/processes"}>
                    <Network />
                    <span>{"Processes"}</span>
                    <span className="ml-auto text-xs">
                      { isLoading ? <Loader2 size={16} className="animate-spin ml-1" /> : processCount}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem key={"automations"}>
                <SidebarMenuButton asChild isActive={path === "/automations"}>
                  <Link to={"/automations"}>
                    <Cog />
                    <span>{"Automations"}</span>
                    <span className="ml-auto text-xs">
                      { isLoading ? <Loader2 size={16} className="animate-spin ml-1" /> : automationCount}
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {isAdmin && (
                <SidebarMenuItem key={"settings"}>
                  <SidebarMenuButton asChild isActive={path === "/settings"}>
                    <Link to={"/settings"}>
                      <Settings />
                      <span>{"Settings"}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        {isGlobalAdmin && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>ADMIN</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem key={"global-admin-portal"}>
                    <SidebarMenuButton asChild>
                      <a
                        href={`https://${window.location.hostname.replace(/^aoc\./, 'global-admin.')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Shield />
                        <span>Global Admin Portal</span>
                        <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
        <SidebarSeparator />
        <SidebarGroup>
          <SidebarGroupLabel>SHORTCUTS</SidebarGroupLabel>
          <SidebarGroupContent>
            <NavTreeView />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarFooterMenu
              name={user?.name}
              email={user?.email}
              avatar={user?.avatar}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}